import type { MaterialInfo, PartDefinition } from '@rogue/rackbuilder'
import { AdjustOnlyVisual } from '../../../constants'
import { getRackModel } from '../../../modelPaths'
import { RackOptions } from '../../../RackTypes'
import { MaterialBuilder } from '../builders/materialBuilder'
import { AccessoryBuilder } from '../builders/accessoryBuilder'
import { FrontCrossmemberBuilder } from '../builders/frontCrossmemberBuilder'
import { MiddleCrossmemberBuilder } from '../builders/middleCrossmemberBuilder'
import { BackCrossmemberBuilder } from '../builders/backCrossmemberBuilder'
import { BarBuilder, PlateAmount } from '../../bar/barBuilder'
import { addPlateStorage } from '../../../utils/plate-storage'

/**
 * Base builder for standard power racks (RM-3, RM-4, RM-6, RML-390, RML-490, RML-690, RML-3)
 */
export class PowerRackBuilder {
  protected config: RackOptions

  // Extracted config values
  protected line: RackOptions['line']
  protected depth: RackOptions['depth']
  protected height: RackOptions['height']
  protected color: RackOptions['color']
  protected hardware: RackOptions['hardware']
  protected numbered: RackOptions['numbered']
  protected sixPost: RackOptions['sixPost']
  protected isRML3: RackOptions['isRML3']
  protected isAddon: boolean

  // Crossmember line for getRackModel path
  protected crossmemberLine: string
  
  // Materials
  protected material: MaterialInfo
  protected crossmemberMaterial: MaterialInfo
  
  // Delegate builders
  protected frontCrossmemberBuilder: FrontCrossmemberBuilder 
  protected backCrossmemberBuilder: BackCrossmemberBuilder   
  protected middleCrossmemberBuilder: MiddleCrossmemberBuilder 
  protected accessoryBuilder: AccessoryBuilder
  protected materialBuilder: MaterialBuilder
  protected addonMaterial: MaterialInfo
  
  constructor(config: RackOptions) {
    this.config = config
    this.line = config.line
    this.depth = config.depth
    this.height = config.height
    this.color = config.color
    this.hardware = config.hardware
    this.numbered = config.numbered || false
    this.sixPost = config.sixPost || false
    this.isRML3 = config.isRML3 || false
    this.isAddon = !!config.isAddon
    
    // Initialize delegate builders
    this.materialBuilder = new MaterialBuilder(config)
    this.addonMaterial = this.materialBuilder.createAddonMaterials()
    this.material = this.isAddon ? this.addonMaterial : this.materialBuilder.createMainMaterial()
    this.crossmemberMaterial = this.isAddon ? this.addonMaterial : this.materialBuilder.createCrossmemberMaterials()
    this.accessoryBuilder = new AccessoryBuilder(config, this.material)
    this.backCrossmemberBuilder = new BackCrossmemberBuilder(config, this.materialBuilder)
    this.frontCrossmemberBuilder = new FrontCrossmemberBuilder(config, this.material, this.materialBuilder)
    this.middleCrossmemberBuilder = new MiddleCrossmemberBuilder(config, this.materialBuilder)

    this.crossmemberLine = `${this.line}_CROSSMEMBERS`
  }
  
  public build(): PartDefinition {
    return this.assembleRack()
  }
  
  // MARK: Assemble
  protected assembleRack(): PartDefinition {
    const leftSide = this.createSide(true)
    const rightSide = this.createSide(false)
    
    // Connect sides
    leftSide.slots['other-side'] = rightSide
    leftSide.id = 'main'
    
    // Add pullup bar to left side
    this.attachFrontCrossmember(leftSide)
    
    // Add middle options (multi-grip, fat/skinny)
    this.attachMiddleCrossmember(leftSide)
    
    // Add nameplate/rear crossmember
    this.attachBackCrossmember(leftSide)
    
    // Add bench if configured
    this.attachBench(leftSide)
    
    return leftSide
  }
  
  // MARK: Create Side
  protected createSide(left: boolean): PartDefinition {
    // Creates one side of the rack (left or right)
    const frontUpright = this.createFrontUpright(left)
    const backUpright = this.createBackUpright(left)
    
    // Create crossmembers
    const topCrossmember = this.createTopCrossmember(left)
    const bottomCrossmember = this.createBottomCrossmember(backUpright)
    
    // Attach crossmembers to front upright
    frontUpright.slots['crossmember-top'] = [topCrossmember]
    frontUpright.slots['crossmember-bottom'] = this.config.frontStabilizer
      ? [bottomCrossmember, this.createFrontStabilizer()]
      : [bottomCrossmember]
    
    // Add branding
    this.addBranding(frontUpright, backUpright, topCrossmember, left)
    
    // Add accessories to uprights
    this.addAccessories(frontUpright, backUpright, left)
    
    // Add storage if configured
    if (this.shouldAddStorageRow()) {
      this.addStorageRow(backUpright, left)
    }
    
    return frontUpright
  }
  
  // MARK: Uprights
  protected createFrontUpright(left: boolean): PartDefinition {
    const sideTransform = this.getSideTransform(left)

    // Handles split black / stainlessUprights material
    const uprightMaterial: MaterialInfo = this.config.stainlessUprights
      ? { ...this.material, main: { name: 'metal', material: 'stainless', color: '#ffffff' } }
      : this.material
  
    return {
      mesh      : this.getUprightMesh(),
      materials : uprightMaterial,
      ...sideTransform,
      slots     : {},
    }
  }
  
  protected createBackUpright(left: boolean): PartDefinition {
    const sideTransform = this.getSideTransform(left)
    
    return {
      mesh      : this.getUprightMesh(),
      anchor    : 'crossmember-bottom',
      materials : this.material,
      ...sideTransform,
      slots     : {},
    }
  }
  
  protected getUprightMesh(): string {
    const type = this.numbered ? 'numbered' : 'standard'

    return getRackModel(this.getUprightLine(), `upright ${type} ${this.height}`)
  }

  protected getUprightLine(): string {
    return `${this.line}_UPRIGHTS`
  }
  
  // MARK: Side Crossmembers
  protected createTopCrossmember(left: boolean): PartDefinition {
    const crossmemberName = this.line === 'RM'
      ? `crossmember_top_${this.depth}`
      : `crossmember_${this.depth}`

    let mesh: string
    if (this.depth === '7') mesh = getRackModel(this.depthCrossmemberLine, 'crossmember top 7')
    else if (this.isRML3) mesh = getRackModel('SPECIALTY_PARTS', 'rml crossmember 2 no hardware')
    else mesh = getRackModel(this.crossmemberLine, crossmemberName)

    const needsOffset = this.line === 'RM' && this.depth === '3' && !left

    return {
      mesh,
      anchor         : 'front',
      slots          : {},
      materials      : this.crossmemberMaterial,
      adjustments    : this.depthCrossmemberNeedsFlip(left) ? [{ type: 'rotate', axis: 'Y', amount: 180 }] : [],
      adjustmentType : AdjustOnlyVisual,
      offset         : needsOffset ? { z: -0.2 } : {},
    }
  }

  // Cave owns the Monster 70" beam and goes on reading it from its own file; HybridRigBuilder overrides this so no rig pulls CAVE_RM in for one crossmember.
  protected get depthCrossmemberLine(): string {
    return this.line === 'RML' ? 'RML_HYBRID_LARGE' : 'CAVE_RM'
  }

  // AdjustOnlyVisual rotates the meshes about the part's own origin, which both 70" beams put at their span midpoint, so the 180 swaps ends and moves the gusset outboard without changing the span. Regular RM crossmembers flip on the right, the 70" beams (Cave's and the ML one, authored alike) on the left; regular RML crossmembers never flip.
  protected depthCrossmemberNeedsFlip(left: boolean): boolean {
    return this.depth === '7' ? left : (!left && this.line === 'RM')
  }
  
  protected createBottomCrossmember(backUpright: PartDefinition): PartDefinition {
    const crossmemberName = this.line === 'RM' 
      ? `crossmember_bottom_${this.depth}` 
      : `crossmember_${this.depth}`
    
    const mesh = this.isRML3
      ? getRackModel('SPECIALTY_PARTS', 'rml crossmember 2 no hardware')
      : getRackModel(this.crossmemberLine, crossmemberName)
    
    return {
      mesh,
      materials : this.crossmemberMaterial,
      anchor    : 'front',
      slots     : {
        back: backUpright,
      },
    }
  }
  
  protected createFrontStabilizer(): PartDefinition {
    return {
      mesh      : getRackModel(this.crossmemberLine, 'front stabilizer'),
      slots     : {},
      materials : { main: { material: 'mg', color: 'black' } },
    }
  }
  
  // MARK: Branding
  protected addBranding(
    frontUpright: PartDefinition, 
    backUpright: PartDefinition, 
    topCrossmember: PartDefinition,
    left: boolean
  ): void {
    if (this.shouldAddRMBrandingPlate(left)) {
      // Add branding plate to top crossmember
      const brandingPlate: PartDefinition = {
        mesh      : getRackModel('RM_UPRIGHTS', 'Branding_Plate'),
        slots     : {},
        anchor    : 'top',
        materials : { hardware: { material: this.hardware } },
      }

      frontUpright.slots['crossmember-top'] = [topCrossmember, brandingPlate]
    }
    
    if (this.line === 'RML' && !this.config.isAddon) {
      const brandingSticker: PartDefinition = {
        mesh        : getRackModel('RML_UPRIGHTS', 'sticker'),
        slots       : {},
        adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      }
      
      if (left) {
        backUpright.slots.sticker_left = brandingSticker
      } else {
        frontUpright.slots.sticker_right = brandingSticker
      }
    }
  }
  
  // MARK: Accessories
  protected addAccessories(frontUpright: PartDefinition, backUpright: PartDefinition, left: boolean): void {
    // Safeties
    if (this.config.safeties) {
      frontUpright.slots['safety-high'] = this.accessoryBuilder.createSafety(left)
    }

    const jcup = this.accessoryBuilder.createJCup(left)

    if (this.config.jcup) {
      const jcupUpright = this.shouldPlaceJCupOnBackUpright() ? backUpright : frontUpright
      jcupUpright.slots['jcup-high'] = jcup
    }

    // Add bar to j-cup if configured
    if (this.config.jcup && this.config.sixPost && this.config.bar && left) {
      jcup.slots.bar = this.accessoryBuilder.createBar()
    }
    
    // Spotter arms
    if (this.config.spotterArms && this.shouldAddSpotterArms()) {
      frontUpright.slots['safety-low'] = this.accessoryBuilder.createSpotterArms(left)
    }
    
    // Band pegs
    if (this.config.bandPegs) {
      this.accessoryBuilder.addBandPegs(frontUpright, backUpright, left)
    }
  }
  
  // MARK: Storage
  protected addStorageRow(backUpright: PartDefinition, left: boolean ): void {
    // RM/RML-6 back part
    const thirdRowUpright: PartDefinition = {
      mesh      : this.getUprightMesh(),
      anchor    : 'crossmember-bottom',
      materials : this.material,
      ...this.getSideTransform(left),
      slots     : {},
    }
   
    if (this.config.plateStorage) {
      const leftFirstRowPlates = BarBuilder.createPlates(this.config.plates, PlateAmount.HeavyPlates, true)
      const rightFirstRowPlates = BarBuilder.createPlates(this.config.plates, PlateAmount.HeavyPlates, false, 6.5)
    
      const leftSecondRowPlates =   BarBuilder.createPlates(this.config.plates, PlateAmount.LightPlates, true, -0.5)
      const rightSecondRowPlates =   BarBuilder.createPlates(this.config.plates, PlateAmount.LightPlates, false, 6)
    
      const storagePins = addPlateStorage(
        this.config,
        left,
        this.material,
        this.getSideTransform(left),
        this.config.plateStorage,
        leftFirstRowPlates,
        rightFirstRowPlates,
        leftSecondRowPlates,
        rightSecondRowPlates
      )

      // Add storage pin for plates to the back upright
      if (this.sixPost) {
        thirdRowUpright.slots.storage = storagePins
      }

      this.onStoragePinsCreated(storagePins, backUpright, left)
    }
    
    if (this.sixPost) {
    // Connect back upright to third row with crossmembers
      const crossmemberName = this.line === 'RM' ? 'crossmember_bottom_2' : 'crossmember_2'

      backUpright.slots['crossmember-bottom'] = {
        mesh  : getRackModel(this.crossmemberLine, crossmemberName),
        slots : {
          back: thirdRowUpright,
        },
        anchor    : 'front',
        materials : this.crossmemberMaterial,
      }

      const topCrossmemberName = this.line === 'RM' ? 'crossmember_top_2' : 'crossmember_2'
    
      backUpright.slots['crossmember-top'] = {
        mesh      : getRackModel(this.crossmemberLine, topCrossmemberName),
        slots     : {},
        anchor    : 'front',
        materials : this.crossmemberMaterial,
        ...this.getSideTransform(left),
      }
    }
  }
  
  // MARK: Top Xmember
  protected get crossmemberWidth(): string | undefined {
    return undefined
  }

  // Attaches pullup bar or front crossmember to left side
  protected attachFrontCrossmember(leftSide: PartDefinition): void {
    const frontCrossmember = this.frontCrossmemberBuilder.createFrontCrossmember(this.crossmemberWidth)

    if (this.config.front && frontCrossmember) {
      leftSide.slots.top = frontCrossmember
    }
  }
  
  protected attachMiddleCrossmember(leftSide: PartDefinition): void {
    const middleCrossmember = this.middleCrossmemberBuilder.createMiddleCrossmember()

    if (this.config.middle && middleCrossmember) {
      const topCrossmember = leftSide.slots['crossmember-top'] as PartDefinition[]
      topCrossmember[0].slots.first = middleCrossmember
    }
  }
  
  // Attaches back crossmember or nameplate
  // MARK: Back Xmember
  protected attachBackCrossmember(leftSide: PartDefinition): void {
    if (!this.config.back) return
    const backUpright = this.getBackUpright(leftSide)

    // Add back crossmember
    const backCrossmember = this.backCrossmemberBuilder.createBackCrossmember(undefined, undefined, this.crossmemberWidth)
    if (backCrossmember) {
      backUpright.slots.top = backCrossmember
    }

    // Add back stabilizer
    const backStabilizer = this.backCrossmemberBuilder.createBackStabilizer() 
    if (backStabilizer) {
      backUpright.slots.storage = backStabilizer
    }
  }
  
  protected attachBench(leftSide: PartDefinition): void {
    if (!this.config.bench) return
    
    const backUpright = this.getBackUpright(leftSide)
    backUpright.slots.bench = this.accessoryBuilder.createBench()
  }
  
  // Gets back upright from left side structure
  protected getBackUpright(leftSide: PartDefinition): PartDefinition {
    return (leftSide.slots['crossmember-bottom'] as PartDefinition[])[0].slots.back as PartDefinition
  }
  
  // Gets side transform (rotation/flip for right side)
  protected getSideTransform(left: boolean): Partial<PartDefinition> {
    if (left) return {}

    return {
      adjustments    : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      adjustmentType : AdjustOnlyVisual,
    }
  }

  // ─── Extension Hooks ──────────────────────────────────────────────────────
  // Override in subclasses to add variant-specific behaviour without
  // touching the core power rack assembly logic.

  /** Whether addStorageRow should run for this side. */
  protected shouldAddStorageRow(): boolean {
    return !!this.sixPost
  }

  /** Whether to attach the RM branding plate to the top crossmember. */
  protected shouldAddRMBrandingPlate(left: boolean): boolean {
    return this.line === 'RM' && !left
  }

  /** Whether to add spotter arms. Subclasses can suppress when another accessory occupies the same slot. */
  protected shouldAddSpotterArms(): boolean {
    return true
  }

  /** Standard Power Racks mount j-cups on the back upright regardless of post count. */
  protected shouldPlaceJCupOnBackUpright(): boolean {
    return true
  }

  /**
   * Called after storagePins are built inside addStorageRow.
   * No-op by default — subclasses can add spacers, half-rack slot assignments, etc.
   */
  protected onStoragePinsCreated(storagePins: PartDefinition[], backUpright: PartDefinition, left: boolean): void {}
}